import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getSession } from '../services/authService';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import AdminClassPurgeSection from '../components/admin/AdminClassPurgeSection';
import {
  fetchSchoolUserAdminStats,
  fetchUserStatDetail,
  displayTeamMembershipRole,
  ACCOUNT_PASS_CATEGORY_LABEL,
  ACCOUNT_PASS_CATEGORY_PARTS,
  type AccountPassCategory,
  type SchoolUserAdminStats,
  type TeamDetailRow,
  type TeamMemberSummaryRow,
  type UserStatDetailKind,
  type UserStatDetailPayload,
} from '../services/adminUserStatsService';
import { listPlatformManagers } from '../services/platformManagerService';
import AdminEventGroupPassIssuancePair from '../components/admin/AdminEventGroupPassIssuancePair';
import AdminPaidEventPassRegisterSection from '../components/admin/AdminPaidEventPassRegisterSection';
import AdminPlatformStaffModals from '../components/admin/AdminPlatformStaffModals';
import { PLATFORM_CONTENT_SCOPE_KEY } from '../constants/platformContentScope';
import { clearAdminUserStatsCaches, loadAdminUserStatsCache } from '../utils/platformGlobalAdminCache';
import { formatLastAccess } from '../utils/formatLastAccess';

function isAdminSessionEmail(email: string): boolean {
  return isPlatformAdminEmail(email);
}

interface AdminUsersAndPassPageProps {
  workspaceId: string;
}

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** 워크스페이스 생성일(날짜만) */
function formatCreatedDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ko-KR', { dateStyle: 'medium' });
}

function StatCard({
  label,
  value,
  hint,
  onOpenDetail,
  detailDisabled,
  categoryBreakdown,
  onOpenCategory,
  adminStaffCount,
  onOpenAdminStaff,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  onOpenDetail?: () => void;
  detailDisabled?: boolean;
  categoryBreakdown?: {
    beta: number;
    free: number;
    eventFree: number;
    eventPaid: number;
    individual: number;
    group: number;
  } | null;
  onOpenCategory?: (category: AccountPassCategory) => void;
  adminStaffCount?: number | null;
  onOpenAdminStaff?: () => void;
}) {
  const clickable = Boolean(onOpenDetail) && !detailDisabled;
  const mainBlock = (
    <>
      <div className="cp-admin-users-pass-stat-value">{value}</div>
      <div className="cp-admin-users-pass-stat-label">{label}</div>
      {hint ? <p className="cp-admin-users-pass-stat-hint">{hint}</p> : null}
      {clickable ? <p className="cp-admin-users-pass-stat-tap-hint">클릭하여 목록 보기</p> : null}
    </>
  );
  const miniGrid = categoryBreakdown ? (
    <div className="cp-admin-users-pass-stat-mini-grid" aria-label="이용권 유형·관리자">
      {(
        [
          { key: 'free', count: categoryBreakdown.free },
          { key: 'beta', count: categoryBreakdown.beta },
          { key: 'eventFree', count: categoryBreakdown.eventFree },
          { key: 'eventPaid', count: categoryBreakdown.eventPaid },
          { key: 'individual', count: categoryBreakdown.individual },
          { key: 'group', count: categoryBreakdown.group },
        ] as const
      ).map((item) => {
        const parts = ACCOUNT_PASS_CATEGORY_PARTS[item.key];
        const fullLabel = ACCOUNT_PASS_CATEGORY_LABEL[item.key];
        return (
          <button
            key={item.key}
            type="button"
            className={
              'cp-admin-users-pass-stat-mini' +
              (onOpenCategory && !detailDisabled ? ' is-clickable' : '')
            }
            disabled={detailDisabled || !onOpenCategory}
            title={`${fullLabel} 목록 보기`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenCategory?.(item.key);
            }}
          >
            <span className="cp-admin-users-pass-stat-mini-count">{item.count}명</span>
            <span className="cp-admin-users-pass-stat-mini-label">
              <span className="cp-admin-users-pass-stat-mini-tier">{parts.tier}</span>
              <span className="cp-admin-users-pass-stat-mini-detail">{parts.detail}</span>
            </span>
          </button>
        );
      })}
      {onOpenAdminStaff ? (
        <button
          type="button"
          className="cp-admin-users-pass-stat-mini cp-admin-users-pass-stat-mini--admin is-clickable"
          title="관리자·매니저 목록 보기"
          onClick={(e) => {
            e.stopPropagation();
            onOpenAdminStaff();
          }}
        >
          <span className="cp-admin-users-pass-stat-mini-count">
            {adminStaffCount == null ? '—' : `${adminStaffCount}명`}
          </span>
          <span className="cp-admin-users-pass-stat-mini-label">
            <span className="cp-admin-users-pass-stat-mini-tier">관리자</span>
            <span className="cp-admin-users-pass-stat-mini-detail">매니저</span>
          </span>
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      className={
        'cp-admin-users-pass-stat-card' +
        (clickable ? ' is-clickable' : '') +
        (categoryBreakdown ? ' cp-admin-users-pass-stat-card--breakdown' : '')
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onOpenDetail : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenDetail?.();
              }
            }
          : undefined
      }
    >
      {categoryBreakdown ? (
        <>
          <div className="cp-admin-users-pass-stat-card__main">{mainBlock}</div>
          {miniGrid}
        </>
      ) : (
        <>
          <div className="cp-admin-users-pass-stat-card__top">{mainBlock}</div>
        </>
      )}
    </div>
  );
}

function modalTitle(kind: UserStatDetailKind, passCategory: AccountPassCategory | null): string {
  switch (kind) {
    case 'allAccounts':
      return passCategory
        ? `전체 등록 계정 · ${ACCOUNT_PASS_CATEGORY_LABEL[passCategory]}`
        : '전체 등록 계정';
    case 'teamsWithMembers':
      return '팀 목록 · 소속 계정';
  }
}

function UserIdentityBlock({
  user_id,
  email,
  display_name,
  teamTeamLine,
  teamHeadTone = 'default',
  passCategory,
  staffRole = null,
}: {
  user_id: string;
  email: string;
  display_name: string;
  /** 예: "1학년팀 / 팀장" — 있으면 계정 위에 크게 표시 */
  teamTeamLine?: string | null;
  /** `muted`: 팀 없음(개별 이용) 등 연한 회색, 글자 크기는 동일 */
  teamHeadTone?: 'default' | 'muted';
  passCategory?: AccountPassCategory | null;
  staffRole?: 'admin' | 'manager' | null;
}) {
  const line = (teamTeamLine ?? '').trim();
  const resolvedStaff: 'admin' | 'manager' | null =
    staffRole ?? (isAdminSessionEmail(email) ? 'admin' : null);
  const headClass =
    'cp-admin-users-pass-user-team-head' +
    (teamHeadTone === 'muted' ? ' cp-admin-users-pass-user-team-head--solo' : '');
  return (
    <div className="cp-admin-users-pass-user-block">
      {line || passCategory || resolvedStaff ? (
        <div className="cp-admin-users-pass-user-head-row">
          <div className="cp-admin-users-pass-user-head-left">
            {line ? <div className={headClass}>{line}</div> : null}
            {resolvedStaff === 'admin' ? (
              <span className="cp-admin-users-pass-user-admin-badge" title="플랫폼 관리자">
                관리자
              </span>
            ) : null}
            {resolvedStaff === 'manager' ? (
              <span className="cp-admin-users-pass-user-manager-badge" title="플랫폼 매니저">
                매니저
              </span>
            ) : null}
          </div>
          {passCategory ? (
            <span
              className={`cp-admin-users-pass-user-pass-pill cp-admin-users-pass-user-pass-pill--${passCategory}`}
            >
              {ACCOUNT_PASS_CATEGORY_LABEL[passCategory]}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="cp-admin-users-pass-user-email-row">
        <span className="cp-admin-users-pass-user-k">계정</span>
        <span className="cp-admin-users-pass-user-email-lg">{email}</span>
      </div>
      <div className="cp-admin-users-pass-user-name-row">
        <span className="cp-admin-users-pass-user-k">이름</span>
        <span className="cp-admin-users-pass-user-name-md">{display_name}</span>
      </div>
      <div className="cp-admin-users-pass-user-uid-row">
        <span className="cp-admin-users-pass-user-k">UID</span>
        <span className="cp-admin-users-pass-user-uid-sm" title="사용자 UID">
          {user_id}
        </span>
      </div>
    </div>
  );
}

function TeamsWithMembersSplitBody({
  teams,
  membersByTeamId,
}: {
  teams: TeamDetailRow[];
  membersByTeamId: Record<string, TeamMemberSummaryRow[]>;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (teams.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => (prev && teams.some((t) => t.id === prev) ? prev : teams[0].id));
  }, [teams]);

  const selected = teams.find((t) => t.id === selectedId) ?? null;
  const members = selectedId ? (membersByTeamId[selectedId] ?? []) : [];

  return (
    <div className="cp-admin-users-pass-team-split">
      <section
        className="cp-admin-users-pass-team-split__pane cp-admin-users-pass-team-split__pane--left"
        aria-labelledby="cp-admin-team-split-teams-title"
      >
        <h4 className="cp-admin-users-pass-team-split__pane-title" id="cp-admin-team-split-teams-title">
          팀 목록
        </h4>
        <div className="cp-admin-users-pass-team-split__scroll">
          {teams.length === 0 ? (
            <p className="cp-admin-users-pass-modal-empty cp-admin-users-pass-team-split__empty">등록된 팀이 없습니다.</p>
          ) : (
            <ul className="cp-admin-users-pass-team-split__team-ul">
              {teams.map((t) => {
                const isSel = t.id === selectedId;
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={'cp-admin-users-pass-team-split__team-btn' + (isSel ? ' is-selected' : '')}
                      aria-pressed={isSel}
                      onClick={() => setSelectedId(t.id)}
                    >
                      <span className="cp-admin-users-pass-team-split__team-name">{t.name}</span>
                      <span className="cp-admin-users-pass-team-split__team-code">코드 {t.code}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
      <section
        className="cp-admin-users-pass-team-split__pane cp-admin-users-pass-team-split__pane--right"
        aria-labelledby="cp-admin-team-split-members-title"
        aria-live="polite"
      >
        <h4 className="cp-admin-users-pass-team-split__pane-title" id="cp-admin-team-split-members-title">
          소속 계정
        </h4>
        {!selected ? (
          <div className="cp-admin-users-pass-team-split__scroll">
            <p className="cp-admin-users-pass-modal-empty">왼쪽에서 팀을 선택하세요.</p>
          </div>
        ) : (
          <>
            <div className="cp-admin-users-pass-team-split__right-head">
              <span className="cp-admin-users-pass-team-split__right-title">{selected.name}</span>
              <span className="cp-admin-users-pass-team-split__right-sub">
                인원 제한: {selected.member_limit != null ? `${selected.member_limit}명` : '없음'} · 생성{' '}
                {formatTs(selected.created_at)}
              </span>
            </div>
            <div className="cp-admin-users-pass-team-split__scroll">
              {members.length === 0 ? (
                <p className="cp-admin-users-pass-modal-empty">승인된 소속 계정이 없습니다.</p>
              ) : (
                <ul className="cp-admin-users-pass-modal-list cp-admin-users-pass-team-split__member-ul">
                  {members.map((m) => (
                    <li key={m.user_id} className="cp-admin-users-pass-modal-li">
                      <UserIdentityBlock
                        user_id={m.user_id}
                        email={m.email}
                        display_name={m.display_name}
                        teamTeamLine={`${selected.name} / ${m.role_display}`}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function DetailListBody({
  payload,
  passCategoryFilter,
  managerUserIds,
}: {
  payload: UserStatDetailPayload;
  passCategoryFilter: AccountPassCategory | null;
  managerUserIds?: Set<string>;
}) {
  if (payload.kind === 'allAccounts') {
    const rows = passCategoryFilter
      ? payload.rows.filter((r) => r.pass_category === passCategoryFilter)
      : payload.rows;
    if (rows.length === 0) {
      return (
        <p className="cp-admin-users-pass-modal-empty">
          {passCategoryFilter
            ? `${ACCOUNT_PASS_CATEGORY_LABEL[passCategoryFilter]} 유형 계정이 없습니다.`
            : '등록된 계정이 없습니다.'}
        </p>
      );
    }
    return (
      <div className="cp-admin-users-pass-all-accounts-scroll">
        <ul className="cp-admin-users-pass-modal-list">
          {rows.map((r) => {
            const hasTeam = Boolean(r.team_name);
            const teamLine = hasTeam
              ? `${r.team_name} / ${displayTeamMembershipRole({
                  user_id: r.user_id,
                  role: r.team_role ?? '',
                  team_created_by: r.team_created_by,
                })}`
              : '개별 이용';
            const staffRole = isAdminSessionEmail(r.email)
              ? 'admin'
              : managerUserIds?.has(r.user_id)
                ? 'manager'
                : null;
            return (
              <li key={r.user_id} className="cp-admin-users-pass-modal-li">
                <UserIdentityBlock
                  user_id={r.user_id}
                  email={r.email}
                  display_name={r.display_name}
                  teamTeamLine={teamLine}
                  teamHeadTone={hasTeam ? 'default' : 'muted'}
                  passCategory={r.pass_category}
                  staffRole={staffRole}
                />
                <div className="cp-admin-users-pass-modal-li-meta cp-admin-users-pass-modal-li-meta--after-user">
                  생성 {formatCreatedDateOnly(r.workspace_created_at)}
                  {' · '}
                  마지막 접속 {formatLastAccess(r.last_sign_in_at, r.last_active_on)}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (payload.kind === 'teamsWithMembers') {
    return null;
  }

  return <p className="cp-admin-users-pass-modal-empty">해당 항목이 없습니다.</p>;
}

const AdminUsersAndPassPage: React.FC<AdminUsersAndPassPageProps> = ({ workspaceId }) => {
  const [sessionEmail, setSessionEmail] = useState('');
  const [stats, setStats] = useState<SchoolUserAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [detailKind, setDetailKind] = useState<UserStatDetailKind | null>(null);
  const [detailPassCategory, setDetailPassCategory] = useState<AccountPassCategory | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailPayload, setDetailPayload] = useState<UserStatDetailPayload | null>(null);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [managerUserIds, setManagerUserIds] = useState<Set<string>>(() => new Set());
  const [adminStaffCount, setAdminStaffCount] = useState<number | null>(null);

  const isAdmin = useMemo(() => isAdminSessionEmail(sessionEmail), [sessionEmail]);

  const refreshManagerIds = useCallback(async () => {
    try {
      const mgrs = await listPlatformManagers();
      setManagerUserIds(new Set(mgrs.map((m) => m.user_id)));
      setAdminStaffCount(1 + mgrs.length);
    } catch (e) {
      console.error(e);
      setManagerUserIds(new Set());
      setAdminStaffCount(1);
    }
  }, []);

  const handleManagersChanged = useCallback((mgrs: { user_id: string }[]) => {
    setManagerUserIds(new Set(mgrs.map((m) => m.user_id)));
    setAdminStaffCount(1 + mgrs.length);
  }, []);

  useEffect(() => {
    let c = false;
    void getSession()
      .then((s) => {
        if (!c) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (!c) setSessionEmail('');
      });
    return () => {
      c = true;
    };
  }, []);

  const loadStats = useCallback(async (options?: { forceRefresh?: boolean }) => {
    const sid = workspaceId.trim();
    if (!sid) return;
    const forceRefresh = options?.forceRefresh === true;
    let hasCache = false;
    if (forceRefresh) {
      clearAdminUserStatsCaches(sid);
    } else {
      const cached = loadAdminUserStatsCache(sid);
      if (cached.found) {
        // 캐시를 즉시 표시하되, 아래에서 head(계정수·팀수·이용권 최신 갱신시각) 비교로 백그라운드 재검증한다.
        setStats(cached.stats);
        setLoading(false);
        setError(null);
        hasCache = true;
      }
    }
    if (!hasCache) setLoading(true);
    setError(null);
    try {
      const s = await fetchSchoolUserAdminStats(sid, forceRefresh ? { forceRefresh: true } : undefined);
      setStats(s);
    } catch (e) {
      if (!hasCache) {
        setStats(null);
        setError(e instanceof Error ? e.message : '통계를 불러오지 못했습니다.');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!isAdmin) return;
    void loadStats();
    void refreshManagerIds();
  }, [isAdmin, loadStats, refreshManagerIds]);

  useEffect(() => {
    if (!detailKind) return;
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailPayload(null);
    void (async () => {
      try {
        const payload = await fetchUserStatDetail(workspaceId, detailKind);
        if (!cancelled) {
          setDetailPayload(payload);
          setDetailLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setDetailError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
          setDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detailKind, workspaceId]);

  const closeDetail = useCallback(() => {
    setDetailKind(null);
    setDetailPassCategory(null);
    setDetailPayload(null);
    setDetailError(null);
    setDetailLoading(false);
  }, []);

  const openAllAccounts = useCallback((category: AccountPassCategory | null = null) => {
    setDetailPassCategory(category);
    setDetailKind('allAccounts');
  }, []);

  useEffect(() => {
    if (!detailKind) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailKind, closeDetail]);

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-admin-users-pass-title">
        <h2 id="cp-admin-users-pass-title" className="cp-page-title">
          이용자 및 이용권 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-admin-users-pass-page--edu"
      aria-labelledby="cp-admin-users-pass-title"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-users-pass-title">이용자 및 이용권 관리</h2>
        </div>
      </div>

      <div className="cp-admin-users-pass-sections">
        <article
          className="cp-dashboard-block cp-admin-users-pass-block cp-admin-users-pass-stats-block"
          aria-labelledby="cp-admin-users-pass-stats-head"
        >
          <h3 className="cp-dashboard-block__head" id="cp-admin-users-pass-stats-head">
            <span className="cp-dashboard-block__head-title">이용자 통계</span>
            <button
              type="button"
              className="cp-btn-secondary cp-admin-users-pass-stats-refresh-btn"
              onClick={() => void loadStats({ forceRefresh: true })}
              disabled={loading}
              aria-label="이용자 통계 집계 새로고침"
            >
              집계 새로고침
            </button>
          </h3>
          <div className="cp-dashboard-block__body cp-admin-users-pass-block-body">
            {loading ? (
              <p className="cp-dashboard-placeholder" role="status">
                불러오는 중…
              </p>
            ) : error ? (
              <p className="cp-dashboard-placeholder" role="alert">
                {error}
              </p>
            ) : stats ? (
              <>
                <div className="cp-admin-users-pass-stat-grid">
                  <StatCard
                    label="전체 등록 계정"
                    value={stats.registeredAccountCount === null ? '—' : `${stats.registeredAccountCount}명`}
                    hint={
                      stats.registeredAccountCount === null
                        ? 'DB에 admin_platform 마이그레이션이 적용되지 않았거나 조회에 실패했습니다.'
                        : undefined
                    }
                    categoryBreakdown={stats.passCategoryCounts}
                    onOpenDetail={() => openAllAccounts(null)}
                    onOpenCategory={(cat) => openAllAccounts(cat)}
                    detailDisabled={stats.registeredAccountCount === null}
                    adminStaffCount={adminStaffCount}
                    onOpenAdminStaff={() => setStaffModalOpen(true)}
                  />
                  <StatCard
                    label="팀 및 소속 계정"
                    value={`${stats.teamCount}팀`}
                    onOpenDetail={() => setDetailKind('teamsWithMembers')}
                  />
                </div>
              </>
            ) : null}
          </div>
        </article>

        <section
          className="cp-admin-users-pass-category"
          aria-labelledby="cp-admin-users-pass-code-issue-cat"
        >
          <h2 id="cp-admin-users-pass-code-issue-cat" className="cp-admin-users-pass-category-title">
            이용권 코드 발급 · 유료 이벤트 카드
          </h2>
          <AdminEventGroupPassIssuancePair
            workspaceId={workspaceId}
            leadingSlot={
              <AdminPaidEventPassRegisterSection
                workspaceId={PLATFORM_CONTENT_SCOPE_KEY}
                legacyMigrateFromWorkspaceId={workspaceId}
              />
            }
          />
        </section>
        <AdminClassPurgeSection />
      </div>

      {detailKind ? (
        <div
          className="cp-modal-backdrop"
          role="presentation"
          onClick={closeDetail}
          onKeyDown={(e) => {
            if (e.key === 'Escape') closeDetail();
          }}
        >
          <div
            className={
              'cp-modal cp-admin-users-pass-detail-modal' +
              (detailKind === 'teamsWithMembers' ? ' cp-admin-users-pass-detail-modal--team-split' : '') +
              (detailKind === 'allAccounts' ? ' cp-admin-users-pass-detail-modal--all-accounts' : '')
            }
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-admin-users-pass-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cp-admin-users-pass-detail-title">
              {modalTitle(detailKind, detailPassCategory)}
            </h3>
            <div className="cp-modal-body cp-admin-users-pass-detail-modal-body">
              {detailLoading ? (
                <p className="cp-dashboard-placeholder" role="status">
                  불러오는 중…
                </p>
              ) : detailError ? (
                <p className="cp-dashboard-placeholder" role="alert">
                  {detailError}
                </p>
              ) : detailPayload && detailPayload.kind === 'teamsWithMembers' ? (
                <TeamsWithMembersSplitBody
                  teams={detailPayload.teams}
                  membersByTeamId={detailPayload.membersByTeamId}
                />
              ) : detailPayload ? (
                <DetailListBody
                  payload={detailPayload}
                  passCategoryFilter={detailPassCategory}
                  managerUserIds={managerUserIds}
                />
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={closeDetail}>
                닫기
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdminPlatformStaffModals
        open={staffModalOpen}
        workspaceId={workspaceId}
        onClose={() => setStaffModalOpen(false)}
        onManagersChanged={handleManagersChanged}
      />
    </section>
  );
};

export default AdminUsersAndPassPage;
